/**
 * Search palette — public surface (Track 3.L; command catalogue removed in Track 4.5.F).
 *
 * Given a query string + the local library snapshot, returns a flat list of
 * PaletteResult objects sorted by fzf score descending.
 *
 * Data sources (all client-side, no server round-trip):
 *   - Recipes from BackendApi.recipe.list()
 *   - Artefacts from BackendApi.artefact.list()
 *   - Batches from BackendApi.batch.list()
 *
 * Variations are NOT a palette result kind (2026-07-10 versions-first cleanup).
 * Variations were removed from the UI on 2026-07-09; the palette is recipes +
 * artefacts + batches only. The kebab escape hatch is the sole legacy variation
 * surface.
 *
 * NOTE (Track 4.5.F): The static command catalogue (> prefix) has been removed
 * from MVP. The full palette layer with prefix syntax and command execution is
 * wishlisted at .plan/wishlist/keyboard-shortcuts-and-palette.md.
 *
 * Spec refs:
 *   design.md §13a.4 — palette behaviour
 *   design.md §6.5 — keyboard policy (no shortcuts at MVP)
 *   architecture.md §4.24 — palette as a client-only feature
 *   branding.md §5.2.1 — banned words
 *   branding.md §8 — vocabulary table
 */

import { fzfSort } from "./fzf-scorer.js";
import type { RecipeRow } from "../api/recipes.js";
import type { ArtefactRow } from "../api/artefacts.js";
import type { BatchRow } from "../api/batches.js";

// ── Result kinds ──────────────────────────────────────────────────────────────

export interface RecipePaletteResult {
  kind: "recipe";
  uuid: string;
  label: string;
  secondary: string | null;
  score: number;
  matchIndices: number[];
}

export interface ArtefactPaletteResult {
  kind: "artefact";
  uuid: string;
  label: string;
  secondary: string | null;
  score: number;
  matchIndices: number[];
}

export interface BatchPaletteResult {
  kind: "batch";
  uuid: string;
  label: string;
  secondary: string | null;
  score: number;
  matchIndices: number[];
}

export type PaletteResult = RecipePaletteResult | ArtefactPaletteResult | BatchPaletteResult;

// ── Library snapshot ──────────────────────────────────────────────────────────

/**
 * The locally-cached snapshot the palette operates on.
 * Callers provide this from their in-memory state — no fetch is made here.
 */
export interface LibrarySnapshot {
  recipes: RecipeRow[];
  artefacts: ArtefactRow[];
  batches: BatchRow[];
}

// ── Main query function ───────────────────────────────────────────────────────

/**
 * Fuzzy-match `query` against the library snapshot.
 * Returns at most `limit` results sorted by score descending.
 *
 * An empty query returns an empty list (not all items — the search starts
 * blank per design.md §13a.4).
 *
 * No command catalogue at MVP (Track 4.5.F scope-cut). Plain text input is
 * matched against recipe titles, artefact names, and batch names. The `>`,
 * `:`, and `#` characters are treated as literal text, not prefix operators.
 */
export function getCommands(query: string, snapshot: LibrarySnapshot, limit = 20): PaletteResult[] {
  const q = query.trim();

  if (q.length === 0) {
    return [];
  }

  const results: PaletteResult[] = [];

  // ── Recipes ──────────────────────────────────────────────────────────────
  const recipeHits = fzfSort(q, snapshot.recipes, (r) => r.title);
  for (const { item, match } of recipeHits) {
    results.push({
      kind: "recipe",
      uuid: item.uuid,
      label: item.title,
      secondary: item.slug,
      score: match.score + 2, // slight boost so recipes outrank other results at same score
      matchIndices: match.indices,
    });
  }

  // Also try matching recipe slug.
  const recipeSlugHits = fzfSort(q, snapshot.recipes, (r) => r.slug);
  for (const { item, match } of recipeSlugHits) {
    // Avoid duplicate — check if this recipe UUID is already in results.
    if (!results.some((r) => r.kind === "recipe" && r.uuid === item.uuid)) {
      results.push({
        kind: "recipe",
        uuid: item.uuid,
        label: item.title,
        secondary: item.slug,
        score: match.score,
        matchIndices: match.indices,
      });
    }
  }

  // ── Artefacts ─────────────────────────────────────────────────────────────
  const artefactHits = fzfSort(q, snapshot.artefacts, (a) => a.name);
  for (const { item, match } of artefactHits) {
    results.push({
      kind: "artefact",
      uuid: item.uuid,
      label: item.name,
      secondary: item.slug,
      score: match.score,
      matchIndices: match.indices,
    });
  }

  // ── Batches ───────────────────────────────────────────────────────────────
  const batchHits = fzfSort(q, snapshot.batches, (b) => b.name);
  for (const { item, match } of batchHits) {
    results.push({
      kind: "batch",
      uuid: item.uuid,
      label: item.name,
      secondary: item.slug,
      score: match.score,
      matchIndices: match.indices,
    });
  }

  // ── Sort + limit ──────────────────────────────────────────────────────────
  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit);
}

// ── Re-exports ────────────────────────────────────────────────────────────────

export { fzfScore, fzfSort } from "./fzf-scorer.js";
export type { LibrarySnapshot as PaletteLibrarySnapshot };
